import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { DEFAULT_TIME_FRAME } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/impact-projections-v2/constants';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
// import { RenderConfidenceInterval } from './ConfidenceInterval';TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';TODO: MVP-5260 - Bring Confidence Interval back to charts
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps } from './types';
import {
  getChartDataFromGraphFact,
  getXAxisDomainForScenarioGraph,
  hasInterpolatedValues,
  intrapolatedValues,
  kgToTonneFormatter,
  minMaxAxisDomain,
  sliceSortedChartDataToDateRange,
  sortChartData,
} from './utils';

const chartColors = {
  carbon: { stroke: '#EAC745', fill: '#FFDD5F' },
  cbg_historic: { stroke: '#CCC3A3', fill: '#9C916B' },
  confidenceInterval: {
    fill: '#C0A53F33',
  },
  estimatedData: { stroke: '#CCC3A3' },
} as const;

const totalFactNames = {
  permanent_soil_carbon_total: 'cbg_historic',
  carbon_storage: 'cbg',
  permanent_soil_carbon_total_benchmark_low: 'cbg_benchmark_low',
  permanent_soil_carbon_total_benchmark_moderate: 'cbg_benchmark_moderate',
  permanent_soil_carbon_total_benchmark_high: 'cbg_benchmark_high',
};

const perHaFactNames = {
  permanent_soil_carbon_per_ha: 'cbg_historic',
  carbon_storage_per_ha: 'cbg',
  permanent_soil_carbon_per_ha_benchmark_low: 'cbg_benchmark_low',
  permanent_soil_carbon_per_ha_benchmark_moderate: 'cbg_benchmark_moderate',
  permanent_soil_carbon_per_ha_benchmark_high: 'cbg_benchmark_high',
};

export const CarbonStorageBGScenarioChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps & { timeFrame: number }> = ({
  analysisType,
  data,
  benchmarks,
  timeFrame = Number(DEFAULT_TIME_FRAME),
  className,
  showTooltip,
  ...delegated
}) => {
  const { t } = useTranslation();
  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? formatUnit(UnitEnum.t) : formatUnit(UnitEnum['t/ha']);

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames, kgToTonneFormatter);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const interpolatedData = intrapolatedValues(sortedChartData, 'cbg_historic');

  const minDate = null;
  const maxDate = timeFrame ? new Date(`${new Date().getFullYear() + timeFrame}`) : null;
  const slicedData = sliceSortedChartDataToDateRange(interpolatedData, minDate, maxDate);

  const firstTick = slicedData.at(0);
  const lastTick = slicedData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }
  const xTicksDomain = getXAxisDomainForScenarioGraph(firstTick.label, lastTick.label, timeFrame);
  const xAxisProps = getXAxisProps(xTicksDomain);

  let chartData = extrapolateReferenceLinesChartData(xTicksDomain, slicedData, 'cbg_benchmark_low');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'cbg_benchmark_moderate');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'cbg_benchmark_high');

  const hasInterpolated = hasInterpolatedValues(chartData, 'cbg_historic');

  return (
    <>
      <Chart.Container
        {...delegated}
        data={chartData}
        config={{
          cbg: {
            label: t('shared.projects.project.impactProjections.labels.optimizedScenario'),
            color: chartColors.carbon.stroke,
          },
          cbg_historic: {
            label: t('shared.projects.project.impactProjections.labels.historicCarbonStorageBelowGround'),
            color: chartColors.cbg_historic.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='cbg' x1='0' y1='-2.6' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.carbon.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.carbon.fill} stopOpacity={0} />
            </linearGradient>
            <linearGradient id='cbg_historic' x1='0' y1='-2.6' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.cbg_historic.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.cbg_historic.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'cbg_historic.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'cbg-historic-scenario-confidence-interval',
          })} */}
          <Area
            type='monotone'
            isAnimationActive={false}
            stroke='var(--color-cbg_historic)'
            strokeWidth={3}
            fill='url(#cbg_historic)'
            fillOpacity={1}
            dataKey='cbg_historic.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='cbg-historic-scenario-area'
          />
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'cbg.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'cbg-scenario-confidence-interval',
          })} */}
          <Area
            type='monotone'
            isAnimationActive={false}
            stroke='var(--color-cbg)'
            strokeWidth={2}
            strokeDasharray='4 4'
            fill='url(#cbg)'
            fillOpacity={1}
            dataKey='cbg.value'
            connectNulls
            activeDot={ChartTooltipDot}
            id='cbg-scenario-area'
          />
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_low.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_low) ? t('global.benchmark.low') : null,
            infoPopoverProps: {
              body: t('shared.ncaDetail.details.carbon.explainers.cbgBenchmark.tooltips.low'),
            },
            chartData,
            id: 'cbg-scenario-benchmark-low',
          })}
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_moderate.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_moderate) ? t('global.benchmark.moderate') : null,
            infoPopoverProps: {
              body: t('shared.ncaDetail.details.carbon.explainers.cbgBenchmark.tooltips.moderate'),
            },
            chartData,
            id: 'cbg-scenario-benchmark-moderate',
          })}
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_high.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_high) ? t('global.benchmark.high') : null,
            infoPopoverProps: {
              body: t('shared.ncaDetail.details.carbon.explainers.cbgBenchmark.tooltips.high'),
            },
            chartData,
            id: 'cbg-scenario-benchmark-high',
          })}
          <Chart.XAxis {...xAxisProps} className='cbg-scenario-x-axis' />
          <Chart.YAxis domain={minMaxAxisDomain(0.8, 1.2)} className='cbg-scenario-y-axis'>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => <CustomTooltip {...props} unit={unit as UnitEnum} benchmarks={benchmarks} />}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate }: { showEstimate: boolean }) => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke,
  });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('shared.projects.project.impactProjections.labels.historicCarbonStorageBelowGround'),
      color: chartColors.cbg_historic.fill,
    },
    {
      label: t('shared.projects.project.impactProjections.labels.optimizedScenario'),
      color: chartColors.carbon.fill,
      shape: (
        <span className='w-[14px] border-y-[3.5px] border-dotted' style={{ borderColor: chartColors.carbon.stroke }} />
      ),
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
    // ...confidenceIntervalLegendItem,
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' data-testid='carbon-storage-bg-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const cbgScenarioValue = payload?.find((data) => data.dataKey === 'cbg.value');
  const cbgHistoricValue = payload?.find((data) => data.dataKey === 'cbg_historic.value');

  const cbgValue = cbgScenarioValue ?? cbgHistoricValue;

  const cbgDisplay = !isNullish(cbgValue?.value)
    ? `${getDisplayNumber(cbgValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  const cbgBenchmarkType = useBenchmarkForFact(benchmarks, cbgValue?.value);

  const dataPointHasLabel = cbgValue && cbgValue?.payload?.cbg_historic?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? cbgValue.payload.cbg_historic.labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalPct = cbgScenarioValue
  //   ? cbgValue?.payload?.cbg?.confidence_interval_pct
  //   : cbgValue?.payload?.cbg_historic?.confidence_interval_pct;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {cbgBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={cbgBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.carbonStorageBg')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{cbgDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={confidenceIntervalPct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipCarbonStorageBGScenario };
